import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from '../Common/Button'
import { Pressable } from '../Common/Pressable'
import { Checkbox } from '../Common'
import { AppDialog } from '../Common/AppDialog'
import { getScreenGuideConfig } from './screenGuideRegistry'
import {
  areScreenGuidesDisabled,
  hasSeenScreenGuide,
  markScreenGuideSeen,
  setScreenGuidesDisabled,
} from './screenGuidePersistence'
import './screenGuide.css'

type ScreenGuideHostProps = {
  screenId: string | null | undefined
  disabled?: boolean
  className?: string
}

const DUMMY_IMAGE_PREFIX = '/screen-guides/shared/dummy-'

const isPlaceholderOnlyGuide = (config: { slides: Array<{ image: string }> }): boolean =>
  config.slides.length > 0 && config.slides.every((slide) => slide.image.startsWith(DUMMY_IMAGE_PREFIX))

export const ScreenGuideHost = ({ screenId, disabled = false, className }: ScreenGuideHostProps) => {
  const config = useMemo(() => getScreenGuideConfig(screenId), [screenId])
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [neverShowAny, setNeverShowAny] = useState(false)

  useEffect(() => {
    // Suppress auto-open for guides that only ship placeholder screenshots.
    // The help launcher stays clickable so anyone can still preview the copy,
    // but users don't get blocked by a "dummy screenshot" dialog the first
    // time they open a screen whose real guide art hasn't landed yet.
    if (!config || disabled || areScreenGuidesDisabled() || hasSeenScreenGuide(config.id) || isPlaceholderOnlyGuide(config)) {
      setOpen(false)
      return
    }
    setActiveIndex(0)
    setNeverShowAny(false)
    const id = window.setTimeout(() => setOpen(true), 180)
    return () => window.clearTimeout(id)
  }, [config, disabled])

  const finish = useCallback(() => {
    if (!config) return
    if (neverShowAny) setScreenGuidesDisabled(true)
    markScreenGuideSeen(config.id)
    setOpen(false)
  }, [config, neverShowAny])

  const replay = () => {
    if (!config || disabled) return
    setActiveIndex(0)
    setNeverShowAny(false)
    setOpen(true)
  }

  const next = () => {
    if (!config) return
    if (activeIndex >= config.slides.length - 1) {
      finish()
      return
    }
    setActiveIndex((index) => Math.min(index + 1, config.slides.length - 1))
  }

  if (!config || disabled || typeof document === 'undefined') return null

  const activeSlide = config.slides[activeIndex] ?? config.slides[0]

  return (
    <>
      <Pressable
        type="button"
        className={`screenGuideLauncher ${className ?? ''}`}
        onClick={replay}
        aria-label={`Open ${config.title} guide`}
        title={`Open ${config.title} guide`}
      >
        <HelpOutlineRoundedIcon sx={{ fontSize: 20 }} />
      </Pressable>
      <AppDialog
        open={open}
        size="lg"
        title={config.title}
        onDismiss={finish}
        actions={[]}
      >
        <div className="screenGuideDialog">
          <div className="screenGuideMedia">
            <img src={activeSlide.image} alt={activeSlide.alt ?? activeSlide.title} />
          </div>
          <div className="screenGuideContent">
            <p className="screenGuideEyebrow">{config.eyebrow ?? 'Screen guide'}</p>
            {config.description ? <p className="screenGuideSummary">{config.description}</p> : null}
            <article className="screenGuideSlideCopy">
              <strong>{activeSlide.title}</strong>
              <p>{activeSlide.description}</p>
            </article>
            <div className="screenGuideDots" aria-label="Guide progress">
              {config.slides.map((slide, index) => (
                <Pressable
                  key={slide.id}
                  type="button"
                  aria-pressed={index === activeIndex}
                  className={index === activeIndex ? 'isActive' : ''}
                  onClick={() => setActiveIndex(index)}
                  aria-label={`Go to guide slide ${index + 1}`}
                  aria-current={index === activeIndex ? 'step' : undefined}
                />
              ))}
            </div>
            <label className="screenGuideNever">
              <Checkbox
                checked={neverShowAny}
                onChange={(checked) => setNeverShowAny(checked)}
              />
              <span>Never show this dialog for any screen again</span>
            </label>
            <div className="screenGuideActions">
              <Button variant="tertiary" size="sm" onClick={finish}>Skip</Button>
              <Button variant="primary" size="sm" onClick={next}>
                {activeIndex >= config.slides.length - 1 ? 'Done' : 'Next'}
              </Button>
            </div>
          </div>
        </div>
      </AppDialog>
    </>
  )
}

export default ScreenGuideHost
